import { inject, Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpEvent,
	HttpRequest,
	HttpHandler,
	HttpErrorResponse,
	HttpContextToken,
} from '@angular/common/http';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { ToastService } from '../features/shared/services/toast.service';
import { NeptuneError } from '../features/shared/models/Neptune';
import { DialogService } from '@ngneat/dialog';

import { TwoStepVerificationComponent } from '../features/shared/components/two-step-verification/two-step-verification.component';
import { AuthService } from '../features/shared/services/auth.service';

export const SKIP_TOAST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	private _toast = inject(ToastService);
	private _dialog = inject(DialogService);
	private _auth = inject(AuthService);

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				const neptuneError = error.error as NeptuneError;
				const errorTitle = neptuneError?.title || 'UnexpectedError';
				const errorMessage = neptuneError?.message || 'An unexpected error occurred';

				if (error.status === 401) {
					this._auth.logout();
				} else if (error.status === 428) {
					return this.handleMFA(request, next);
				} else if (error.status >= 400 && error.status < 600) {
					if (!request.context.get(SKIP_TOAST)) {
						this._toast.open('error', errorTitle, errorMessage);
					}
				}

				throw error;
			}),
		);
	}

	private handleMFA(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const dialogRef = this._dialog.open(TwoStepVerificationComponent, {
			closeButton: false,
			enableClose: false,
			width: '400px',
		});

		return dialogRef.afterClosed$.pipe(
			switchMap(() => {
				return next.handle(request.clone()).pipe(
					catchError((originalRequestError) => {
						return throwError(() => originalRequestError);
					}),
				);
			}),
		);
	}
}
